// ─────────────────────────────────────────────────────────────
// Employee Kanban board — personal task view with drag-and-drop
// status updates.  Uses HTML5 drag API (no extra deps).
// REALTIME — optimistic updates written to the shared localStorage
// key (nexus_founder_tasks) so founders see changes instantly.
// Replace localStorage + 30s polling with WebSocket in Step 7.
// ─────────────────────────────────────────────────────────────
import { useState, useRef, useCallback } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useRealtime } from '../../context/RealtimeContext'
import { GripVertical, Flag, Calendar } from 'lucide-react'
import type { Task } from '../../types'

type Status = Task['status']

const COLUMNS: { id: Status; label: string; color: string }[] = [
  { id: 'todo',        label: 'To Do',      color: '#555' },
  { id: 'in-progress', label: 'In Progress', color: '#ffc600' },
  { id: 'review',      label: 'In Review',   color: '#ff8c00' },
  { id: 'done',        label: 'Done',        color: '#e8a000' },
]

function priorityColor(p: string) {
  switch (p) {
    case 'urgent': return '#cc3300'
    case 'high':   return '#ff8c00'
    case 'medium': return '#ffc600'
    default:       return '#555'
  }
}

// ── Task card ─────────────────────────────────────────────────
function TaskCard({ task, onDragStart }: {
  task: Task
  onDragStart: (e: React.DragEvent, id: string) => void
}) {
  const isOverdue = task.status !== 'done' && new Date(task.dueDate) < new Date()
  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, task.id)}
      className="rounded-lg p-3 cursor-grab active:cursor-grabbing select-none"
      style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.08)' }}
    >
      <div className="flex items-start gap-2 mb-2">
        <GripVertical size={12} className="mt-0.5 flex-shrink-0" style={{ color: '#333' }} />
        <p className="text-sm font-medium text-white leading-snug flex-1">{task.title}</p>
      </div>
      {task.description && (
        <p className="text-xs ml-5 mb-2 line-clamp-2" style={{ color: '#555' }}>{task.description}</p>
      )}
      <div className="flex items-center gap-2 ml-5 flex-wrap">
        <span className="flex items-center gap-1 text-xs" style={{ color: priorityColor(task.priority) }}>
          <Flag size={10} /> {task.priority}
        </span>
        <span className="flex items-center gap-1 text-xs" style={{ color: isOverdue ? '#cc3300' : '#555' }}>
          <Calendar size={10} />
          {new Date(task.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
          {isOverdue && ' · overdue'}
        </span>
      </div>
    </div>
  )
}

// ── Kanban column ─────────────────────────────────────────────
function KanbanColumn({ col, tasks, isDragOver, onDrop, onDragOver, onDragLeave, onDragStart }: {
  col: typeof COLUMNS[number]
  tasks: Task[]
  isDragOver: boolean
  onDrop: (e: React.DragEvent, status: Status) => void
  onDragOver: (e: React.DragEvent, status: Status) => void
  onDragLeave: () => void
  onDragStart: (e: React.DragEvent, id: string) => void
}) {
  return (
    <div
      onDrop={e => onDrop(e, col.id)}
      onDragOver={e => onDragOver(e, col.id)}
      onDragLeave={onDragLeave}
      className="rounded-xl flex flex-col"
      style={{
        background: '#0f0f0f',
        border: `1px solid ${isDragOver ? col.color : 'rgba(255,198,0,0.12)'}`,
        minHeight: 200,
        transition: 'border-color 0.15s',
      }}
    >
      {/* Column header */}
      <div className="flex items-center justify-between px-4 py-3 border-b"
        style={{ borderColor: 'rgba(255,198,0,0.08)' }}>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ background: col.color }} />
          <span className="text-sm font-semibold text-white">{col.label}</span>
        </div>
        <span className="text-xs font-bold px-1.5 py-0.5 rounded"
          style={{ background: `${col.color}18`, color: col.color }}>
          {tasks.length}
        </span>
      </div>

      {/* Cards */}
      <div className="flex-1 p-3 space-y-2.5">
        {tasks.length === 0 && (
          <div className="rounded-lg border-2 border-dashed h-16 flex items-center justify-center"
            style={{ borderColor: 'rgba(255,198,0,0.08)' }}>
            <span className="text-xs" style={{ color: '#333' }}>Drop here</span>
          </div>
        )}
        {tasks.map(t => (
          <TaskCard key={t.id} task={t} onDragStart={onDragStart} />
        ))}
      </div>
    </div>
  )
}

// ── Board ─────────────────────────────────────────────────────
export default function EmployeeKanban() {
  const { currentUser } = useAuth()
  const { tasksFor, updateTaskStatus } = useRealtime()   // ← REALTIME context
  const [dragOver, setDragOver] = useState<Status | null>(null)
  const draggingId = useRef<string | null>(null)

  const myTasks = tasksFor(currentUser?.name ?? '')

  const handleDragStart = useCallback((e: React.DragEvent, id: string) => {
    draggingId.current = id
    e.dataTransfer.effectAllowed = 'move'
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent, status: Status) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOver(status)
  }, [])

  const handleDragLeave = useCallback(() => setDragOver(null), [])

  const handleDrop = useCallback((e: React.DragEvent, newStatus: Status) => {
    e.preventDefault()
    setDragOver(null)
    const id = draggingId.current
    if (!id) return
    // Optimistic update through context — syncs localStorage + triggers re-render
    updateTaskStatus(id, newStatus)
    draggingId.current = null
  }, [updateTaskStatus])

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>My Tasks</h1>
        <p className="text-sm mt-0.5" style={{ color: '#a0a0a0' }}>
          Drag cards between columns to update status · {myTasks.length} task{myTasks.length !== 1 ? 's' : ''} assigned to you
        </p>
      </div>

      {myTasks.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
            style={{ background: 'rgba(255,198,0,0.06)' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2">
              <polyline points="9 11 12 14 22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
          </div>
          <p className="text-sm" style={{ color: '#333' }}>No tasks assigned yet.</p>
          <p className="text-xs mt-1" style={{ color: '#222' }}>Your founder will assign tasks from the Team page.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {COLUMNS.map(col => (
            <KanbanColumn
              key={col.id}
              col={col}
              tasks={myTasks.filter(t => t.status === col.id)}
              isDragOver={dragOver === col.id}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDragStart={handleDragStart}
            />
          ))}
        </div>
      )}
    </div>
  )
}
